import { FlexCell, FlexRow, TabButton } from '@epam/promo';
import { Outlet, useLocation } from 'react-router-dom';

import { ADMIN } from 'common';
import { useNavigationHandler } from 'hooks';
import { UserRole } from 'models/user.model';
import { useUserStore } from 'store/user';
import { useFeatureTogglesStore } from 'store/feature-toggling';
import { settingsRoutes } from 'router/settingsRoutes';
import type { FeatureName } from 'models/feature-toggling.model';

const AdminManagementCaption = 'Admin management';

type IsFeatureEnabled = (feature: FeatureName) => boolean;

interface SettingsNavHeader {
  caption: string;
  link: {
    pathname: string;
  };
  feature: FeatureName;
}

const SettingsNavHeaders: SettingsNavHeader[] = [
  {
    caption: AdminManagementCaption,
    link: { pathname: settingsRoutes.admins },
    feature: 'adminManagement',
  },
  {
    caption: 'User management',
    link: { pathname: settingsRoutes.users },
    feature: 'userManagement',
  },
  {
    caption: 'Application settings',
    link: { pathname: settingsRoutes.application },
    feature: 'applicationSettings',
  },
  {
    caption: 'Versions',
    link: { pathname: settingsRoutes.versions },
    feature: 'versions',
  },
];

const getNavHeaders = (
  role?: UserRole,
  isFeatureEnabled?: IsFeatureEnabled,
) => {
  let headers = SettingsNavHeaders;

  if (role === ADMIN) {
    headers = headers.filter(
      (h) => ![AdminManagementCaption].includes(h.caption),
    );
  }

  if (isFeatureEnabled) {
    headers = headers.filter((h) => isFeatureEnabled(h.feature));
  }

  return headers;
};

export const SettingsPage = () => {
  const location = useLocation();
  const user = useUserStore((store) => store.user);
  const { isFeatureEnabled } = useFeatureTogglesStore();
  const handleNavigation = useNavigationHandler();

  const headers = getNavHeaders(user?.role, isFeatureEnabled);

  return (
    <FlexCell grow={1}>
      <FlexRow borderBottom padding="18">
        {headers.map(({ caption, link }) => (
          <TabButton
            key={caption}
            caption={caption}
            isActive={location.pathname.startsWith(link.pathname)}
            rawProps={{
              'data-testid': `settings-tab-${caption.toLowerCase().replaceAll(' ', '-')}`,
            }}
            size="48"
            onClick={() => handleNavigation(link.pathname)}
          />
        ))}
      </FlexRow>
      <div style={{ height: 'calc(100vh - 60px - 50px - 2rem)' }}>
        <Outlet />
      </div>
    </FlexCell>
  );
};
